'use client'

import React from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { Box } from '@mantine/core'
import { RebrandVideoRecorderIcon } from '@/components/Icons/Rebrand/VideoRecorderIcon'
import { LabIcon } from '@/components/Icons/LabIcon'
import { RxPrescriptionIcon } from '@/components/Icons/RxPrescriptionIcon'
import { SpecialtyIcon } from '@/components/Icons/SpecialtyIcon'
import { ServiceItem } from '@/types/interfaces/service-item'
import { ServiceItemCategory } from '@/types/enums/ServiceItem'
import classes from './TabbedCard.module.css'
import { PageLocation, StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { cardFadeInVariants, cardContentFadeInVariants } from '@/lib/animations'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { getServiceHref } from '@/lib/getServiceHref'
import { useUserContext } from '@/contexts/UserContext'
import { usePrescriptionRenewalStorefrontNavigation } from '@/hooks/usePrescriptionRenewalStorefrontNavigation'
import { isPrescriptionRenewalStorefrontSkuId } from '@/types/enums/sku-constants'

type ServiceTypeKey = 'virtual-visit' | 'lab-test' | 'prescription' | 'specialist-consult'

interface ServiceTypeConfig {
    displayName: string
    icon: React.ReactNode
    className: string
}

const iconColor = '#1F1E1D'
const iconSize = '13'

const serviceTypeConfig: Record<ServiceTypeKey, ServiceTypeConfig> = {
    'virtual-visit': {
        displayName: 'Virtual visit',
        icon: <RebrandVideoRecorderIcon width="13" height="13" className={classes.iconColor} />,
        className: classes.typeVirtualVisit,
    },
    'lab-test': {
        displayName: 'Lab test',
        icon: <LabIcon color={iconColor} size={iconSize} />,
        className: classes.typeLabTest,
    },
    prescription: {
        displayName: 'Virtual visit',
        icon: <RxPrescriptionIcon color={iconColor} width={iconSize} height={iconSize} />,
        className: classes.typePrescription,
    },
    'specialist-consult': {
        displayName: 'Specialist review',
        icon: <SpecialtyIcon color={iconColor} size={iconSize} />,
        className: classes.typeSpecialistConsult,
    },
}

function getServiceType(service: ServiceItem): ServiceTypeKey {
    if (service.originType === 'chat') {
        return 'virtual-visit'
    }

    switch (service.category) {
        case ServiceItemCategory.lab:
            return 'lab-test'
        case ServiceItemCategory.medication:
            return 'prescription'
        case ServiceItemCategory.visit:
            return 'virtual-visit'
        case ServiceItemCategory.specialty:
            return 'specialist-consult'
        case ServiceItemCategory.condition:
            return 'virtual-visit'
        default:
            return 'virtual-visit'
    }
}

interface TabbedCardProps {
    product: ServiceItem
    index?: number
    pageLocation: PageLocation
    additionalInformation?: Record<string, any>
}

export default function TabbedCard({ product, index = 0, pageLocation, additionalInformation }: TabbedCardProps) {
    const { isSm } = useBreakpoint()
    const { user } = useUserContext()
    const serviceTypeKey = getServiceType(product)
    const serviceType = serviceTypeConfig[serviceTypeKey]
    const catalogHref = getServiceHref(product)
    const isPrescriptionRenewal = isPrescriptionRenewalStorefrontSkuId(product.skuId)
    const { href: prescriptionRenewalHref, isNavigating, navigateToPrescriptionRenewal } =
        usePrescriptionRenewalStorefrontNavigation(!!user && isPrescriptionRenewal)
    const href = isPrescriptionRenewal ? prescriptionRenewalHref : catalogHref
    const copay = product.primaryPrice || ''
    const withoutInsurance = product.secondaryPrice || ''
    const isLongTitle = (product.title?.length ?? 0) > 30

    const cardContent = (
        <motion.div initial="hidden" animate="visible" variants={cardFadeInVariants} className={`${classes.card}`}>
            <motion.div
                initial="hidden"
                animate="visible"
                variants={cardFadeInVariants}
                transition={{
                    ...cardFadeInVariants.visible.transition,
                    delay: index * 0.05 + 0.1,
                }}
            >
                <Box className={`${classes.typeBadge} ${serviceType.className}`}>
                    <Box className={classes.iconWrapper}>{serviceType.icon}</Box>
                    <span>{serviceType.displayName}</span>
                </Box>
            </motion.div>

            <Box className={classes.content}>
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={cardContentFadeInVariants(index * 0.05 + 0.15)}
                >
                    <Box className={`${classes.title} ${isSm ? classes.mobileTitle : ''} ${isLongTitle ? classes.titleLong : ''}`}>{product.title}</Box>
                </motion.div>

                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={cardContentFadeInVariants(index * 0.05 + 0.2)}
                    className={classes.pricing}
                >
                    <Box className={classes.copay}>{copay}</Box>
                    <Box className={classes.withoutInsurance}>{withoutInsurance}</Box>
                </motion.div>
            </Box>
        </motion.div>
    )

    if (href) {
        return (
            <Link
                href={href}
                className={classes.link}
                draggable={false}
                aria-busy={isPrescriptionRenewal && isNavigating ? true : undefined}
                onClick={async (event) => {
                    trackStoreEvent(StoreAnalytics.CLICKED_SKU, {
                        location: pageLocation,
                        additionalInformation,
                        sku: product,
                    })
                    if (isPrescriptionRenewal) {
                        await navigateToPrescriptionRenewal(event)
                    }
                }}
            >
                {cardContent}
            </Link>
        )
    }

    return cardContent
}
